import { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { VideoFrameShaderReadAccess, getVideoFrameShaderAccess } from '@mimir/browser/gldraw/videoFrameShaderReadAccess';
import { WebGlWrapper } from '@mimir/browser/gldraw/webGlWrapper';
import { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';

const MAX_PENDING_FRAMES = 2;

export class RawFrameRenderer {
  private pendingFrames : VideoFrameV2[] = [];

  constructor(
    private webGlWrapper: WebGlWrapper,
    private bufferCache: BufferCache,
  ) { }

  renderFrame(videoFrame: VideoFrameV2): void {
    if (this.pendingFrames.length > MAX_PENDING_FRAMES) {
      throw new Error('This should never happen');
    }
    if (this.pendingFrames.length === MAX_PENDING_FRAMES) {
      this.pendingFrames[MAX_PENDING_FRAMES - 1].buffer.close();
      this.pendingFrames[MAX_PENDING_FRAMES - 1] = videoFrame;
    } else {
      this.pendingFrames.push(videoFrame);
      if (this.pendingFrames.length === 1) {
        requestAnimationFrame(() => this.renderPendingFrame());
      }
    }
  }

  renderPendingFrame() {
    if (this.pendingFrames.length > 0) {
      const [videoFrame] = this.pendingFrames.splice(0, 1);
      this.renderFrameInternal(videoFrame);
      if (this.pendingFrames.length > 0) {
        requestAnimationFrame(() => this.renderPendingFrame());
      }
    }
  }

  renderFrameInternal(videoFrame: VideoFrameV2) {
    //const performanceLogger = createPerformanceLogger();
    const performanceLogger = createDummyPerformanceLogger();
    const gl = this.webGlWrapper.getGlContext();

    performanceLogger('getglContext');

    this.webGlWrapper.throwIfError();

    const shaderReadAccess = getVideoFrameShaderAccess(this.webGlWrapper, videoFrame, this.bufferCache, false);

    performanceLogger('shader read access');

    this.webGlWrapper.throwIfError();

    const fragmentShaderSource = buildSimpleRenderFragmentShaderSource(shaderReadAccess);

    performanceLogger('build shader source');

    this.webGlWrapper.throwIfError();

    const program =
      this.webGlWrapper.getLinkedProgram(
        VERTEX_SHADER_SOURCE_COMMON,
        fragmentShaderSource,
      );

    this.webGlWrapper.throwIfError();

    this.webGlWrapper.throwIfError();

    program.use(gl);

    performanceLogger('get linked program');

    this.webGlWrapper.throwIfError();

    let textureSlot = 0;
    shaderReadAccess.bind(gl, program, textureSlot);

    performanceLogger('bind shader read access');

    this.webGlWrapper.throwIfError();

    const { width, height } = this.webGlWrapper.getCanvasResolution();

    gl.viewport(0, 0, width, height);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    performanceLogger('draw arrays');

    shaderReadAccess.close();

    this.webGlWrapper.throwIfError();

    videoFrame.buffer.close();
    performanceLogger('cleanup');

    // TODO: This seems to be required on firefox, but it blocks everything else so
    // probably not the best idea.
    //gl.finish();
  }
}

export type PerformanceLogger = (name: string) => void;

export function createDummyPerformanceLogger(): PerformanceLogger {
  return (_what: string) => { };
}

export function createPerformanceLogger(): PerformanceLogger {
  let lastMilestone = Date.now();
  return (what: string) => {
    let now = Date.now();
    console.log(`${what} elapsed ${now - lastMilestone} ms`);
    lastMilestone = now;
  };
}

function buildSimpleRenderFragmentShaderSource(shaderReadAccess: VideoFrameShaderReadAccess): string {
  return `\
    #version 300 es
    precision mediump float;

    in vec2 v_uv;

    out vec4 o_color;

    ${shaderReadAccess.readFunctionSource}

    void main() {
      o_color = ${shaderReadAccess.readFunctionName}(v_uv);
    }
  `;
}

const VERTEX_SHADER_SOURCE_COMMON : string = `\
#version 300 es

out vec2 v_uv;

void main() {
  vec2 positions[6] = vec2[](
    vec2(-1.0, -1.0),
    vec2(1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, 1.0)
  );
  vec2 uvs[6] = vec2[](
    vec2(0.0, 1.0),
    vec2(1.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 0.0)
  );
  gl_Position = vec4(positions[gl_VertexID], vec2(0.0, 1.0));
  v_uv = uvs[gl_VertexID];
}`;

